/**
 * Trésorerie Globale — Cockpit CFO consolidé Bank + Wave (Fév 2026).
 * Route : /admin/tresorerie-globale
 *
 * Structure UX (spec CFO) :
 *  - Header : période · Actualiser · Générer rapport · Envoyer · Paramètres · Clôturer
 *  - Hero card : Trésorerie globale + Bank + Wave
 *  - 8 KPI cards : Entrées, Sorties, Frais, Non rapprochées, Exceptions, Statut clôture, etc.
 *  - Onglets : Vue d'ensemble · Comptes · Exceptions · Rapports · Journal audit · Paramètres
 *
 * Réutilise EXCLUSIVEMENT les endpoints /api/treasury/* déjà en place.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Landmark, Wallet, RefreshCw, FileText, Send, Settings2, Lock, Sparkles,
  TrendingUp, TrendingDown, AlertTriangle, CheckCircle2, Calendar,
  Download, Mail, MessageCircle, History, ScrollText, Building2,
  ArrowUpRight, ArrowDownRight, Loader2, X, PlusCircle,
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import PeriodEvolutionCard from '../../components/bank/PeriodEvolutionCard';
import SavedViewsMenu from '../../components/common/SavedViewsMenu';
import StaffRecipientsSelector from '../../components/common/StaffRecipientsSelector';
import RecipientsPreview from '../../components/common/RecipientsPreview';
import {
  MultiTokenInput, validateEmail, validatePhone, normalizeEmail, normalizePhone,
} from '../../components/common/MultiTokenInput';
import TreasuryAnalysisTab from '../../components/admin/TreasuryAnalysisTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmt = (n) => new Intl.NumberFormat('fr-FR').format(Math.round(n || 0));
const MONTHS_FR = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
const periodLabel = (p) => {
  if (!p) return '';
  const [y, m] = p.split('-');
  return `${MONTHS_FR[+m - 1] || m} ${y}`;
};
const nowPeriod = () => new Date().toISOString().slice(0, 7);

/* ═══════════════════════════════════════════════════════════════════════ */

const KpiCard = ({ icon: Icon, label, value, unit = 'XOF', accent = 'blue', subline, testId }) => {
  const themes = {
    blue: 'from-blue-50 to-white border-blue-200 text-blue-600',
    green: 'from-emerald-50 to-white border-emerald-200 text-emerald-600',
    orange: 'from-amber-50 to-white border-amber-200 text-amber-600',
    red: 'from-rose-50 to-white border-rose-200 text-rose-600',
    purple: 'from-violet-50 to-white border-violet-200 text-violet-600',
    slate: 'from-slate-50 to-white border-slate-200 text-slate-600',
  };
  return (
    <div className={`rounded-xl border bg-gradient-to-b ${themes[accent] || themes.blue} p-4 flex flex-col gap-1.5`} data-testid={testId}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide font-medium">
        {Icon ? <Icon className="h-3.5 w-3.5" /> : null}
        <span className="truncate">{label}</span>
      </div>
      <div className="text-lg lg:text-xl font-semibold text-zinc-900">
        {typeof value === 'number' ? fmt(value) : value}
        {unit ? <span className="text-xs font-normal text-zinc-500 ml-1">{unit}</span> : null}
      </div>
      {subline ? <div className="text-xs text-zinc-500">{subline}</div> : null}
    </div>
  );
};

/* ─────── Settings Modal ─────── */
const SettingsModal = ({ open, onClose, settings, onSaved }) => {
  const [form, setForm] = useState(settings || {});
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm(settings || {}); }, [settings, open]);
  if (!open) return null;

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API}/treasury/settings`, form);
      toast({ title: 'Paramètres enregistrés' });
      onSaved(data);
      onClose();
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Échec', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()} data-testid="treasury-settings-modal">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Notifications trésorerie</h3>
            <p className="text-xs text-slate-500">Paramètres &gt; Finance &gt; Notifications trésorerie</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="treasury-settings-close"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-6 space-y-5">
          {/* Activation */}
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
            <div>
              <div className="text-sm font-medium text-slate-800">Envoi automatique</div>
              <div className="text-xs text-slate-500">Activer l&apos;envoi programmé du rapport</div>
            </div>
            <input type="checkbox" checked={!!form.enabled} onChange={(e) => set('enabled', e.target.checked)}
                   className="h-5 w-9 rounded-full cursor-pointer" data-testid="treasury-settings-enabled" />
          </div>

          {/* Fréquence */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-700">Fréquence</label>
              <select value={form.frequency || 'monthly'} onChange={(e) => set('frequency', e.target.value)}
                      className="mt-1 w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm" data-testid="treasury-settings-frequency">
                <option value="daily">Quotidien</option>
                <option value="weekly">Hebdomadaire</option>
                <option value="monthly">Mensuel</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700">Période du rapport</label>
              <select value={form.period_scope || 'month'} onChange={(e) => set('period_scope', e.target.value)}
                      className="mt-1 w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm" data-testid="treasury-settings-scope">
                <option value="day">Jour</option>
                <option value="week">Semaine</option>
                <option value="month">Mois</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700">Heure d&apos;envoi (UTC)</label>
              <input type="number" min="0" max="23" value={form.send_hour_utc || 6}
                     onChange={(e) => set('send_hour_utc', Number(e.target.value))}
                     className="mt-1 w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm" data-testid="treasury-settings-hour" />
            </div>
          </div>

          {/* Destinataires */}
          <div className="space-y-3">
            <div className="text-sm font-semibold text-slate-800">Destinataires (aucun codé en dur)</div>
            {[
              { key: 'finance_team_email', label: 'Email équipe finance', ph: 'finance@baobab...' },
              { key: 'treasurer_email', label: 'Email trésorier', ph: 'tresorier@baobab...' },
              { key: 'cfo_email', label: 'Email CFO / direction', ph: 'cfo@baobab...' },
              { key: 'admin_whatsapp', label: 'WhatsApp administrateur', ph: '+225XXXXXXXXXX' },
            ].map(({ key, label, ph }) => (
              <div key={key}>
                <label className="text-xs font-medium text-slate-700">{label}</label>
                <input type="text" value={form[key] || ''} onChange={(e) => set(key, e.target.value)} placeholder={ph}
                       className="mt-1 w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm" data-testid={`treasury-settings-${key}`} />
              </div>
            ))}
            <div>
              <label className="text-xs font-medium text-slate-700">Emails supplémentaires</label>
              <MultiTokenInput
                value={form.extra_emails || []}
                onChange={(next) => set('extra_emails', next)}
                placeholder="direction@baobab..., auditeur@baobab..."
                validate={validateEmail}
                normalize={normalizeEmail}
                testId="treasury-settings-extras"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700">Numéros WhatsApp supplémentaires</label>
              <MultiTokenInput
                value={form.extra_whatsapps || []}
                onChange={(next) => set('extra_whatsapps', next)}
                placeholder="+225XXXXXXXXXX, +33XXXXXXXXX"
                validate={validatePhone}
                normalize={normalizePhone}
                testId="treasury-settings-extra-whatsapps"
              />
              <p className="text-[10px] text-slate-500 mt-1">
                Le rapport WhatsApp sera envoyé à chaque numéro (administrateur + supplémentaires).
              </p>
            </div>

            {/* Sélection d'utilisateurs staff — récupère automatiquement email + phone */}
            <div className="pt-3 border-t border-slate-100">
              <StaffRecipientsSelector
                value={form.staff_user_ids || []}
                onChange={(ids) => set('staff_user_ids', ids)}
                testIdPrefix="staff"
              />
            </div>

            {/* Prévisualiser destinataires */}
            <div className="pt-2">
              <RecipientsPreview form={form} />
            </div>
          </div>

          {/* Canaux */}
          <div className="space-y-2 pt-3 border-t border-slate-200">
            <div className="text-sm font-semibold text-slate-800">Canaux d&apos;envoi</div>
            {[
              { key: 'send_email', label: 'Activer envoi email' },
              { key: 'attach_pdf_email', label: 'Joindre PDF à l\'email' },
              { key: 'html_in_email_body', label: 'Corps HTML (résumé dans le mail)' },
              { key: 'send_whatsapp', label: 'Activer envoi WhatsApp' },
              { key: 'whatsapp_summary_only', label: 'WhatsApp : résumé texte seul (sinon joindre PDF)' },
              { key: 'keep_history', label: 'Conserver copie dans l\'historique' },
            ].map(({ key, label }) => (
              <label key={key} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input type="checkbox" checked={!!form[key]} onChange={(e) => set(key, e.target.checked)}
                       data-testid={`treasury-settings-toggle-${key}`} />
                {label}
              </label>
            ))}
          </div>
        </div>
        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-slate-300 rounded-lg hover:bg-slate-100">Annuler</button>
          <button onClick={save} disabled={saving}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-slate-400 disabled:cursor-not-allowed"
                  data-testid="treasury-settings-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin inline mr-1" /> : null}
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════ */
/*                            MAIN PAGE                                     */
/* ═══════════════════════════════════════════════════════════════════════ */

export default function TreasureGlobalePage({ embedded = false }) {
  const [period, setPeriod] = useState(nowPeriod());
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('overview');  // overview | evolution | accounts | exceptions | reports | audit | settings

  const [summary, setSummary] = useState({});
  const [kpis, setKpis] = useState({});
  const [exceptions, setExceptions] = useState({ bank_exceptions: [], wave_exceptions: [], total_count: 0 });
  const [closure, setClosure] = useState({});
  const [history, setHistory] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [settings, setSettings] = useState({});
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [periodHistory, setPeriodHistory] = useState({ consolidated: [], by_account: [], wave: [] });
  const [phLoading, setPhLoading] = useState(false);

  const [generating, setGenerating] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendingWA, setSendingWA] = useState(false);
  const [cfoComment, setCfoComment] = useState('');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, k, e, cl, h, al, st] = await Promise.allSettled([
        axios.get(`${API}/treasury/summary`, { params: { period } }),
        axios.get(`${API}/treasury/kpis`, { params: { period } }),
        axios.get(`${API}/treasury/exceptions`, { params: { period } }),
        axios.get(`${API}/treasury/closure-status`, { params: { period } }),
        axios.get(`${API}/treasury/report/history`, { params: { limit: 20 } }),
        axios.get(`${API}/treasury/audit-log`, { params: { limit: 30 } }),
        axios.get(`${API}/treasury/settings`),
      ]);
      if (s.status === 'fulfilled') setSummary(s.value.data);
      if (k.status === 'fulfilled') setKpis(k.value.data);
      if (e.status === 'fulfilled') setExceptions(e.value.data);
      if (cl.status === 'fulfilled') setClosure(cl.value.data);
      if (h.status === 'fulfilled') setHistory(h.value.data?.items || []);
      if (al.status === 'fulfilled') setAuditLog(al.value.data?.items || []);
      if (st.status === 'fulfilled') setSettings(st.value.data);
    } catch (e) {
      // silent
    } finally { setLoading(false); }
  }, [period]);

  const fetchPeriodHistory = useCallback(async () => {
    setPhLoading(true);
    try {
      const { data } = await axios.get(`${API}/treasury/period-history`, {
        params: { months: 12, include_wave: true },
      });
      setPeriodHistory({
        consolidated: data?.consolidated || [],
        by_account: data?.by_account || [],
        wave: data?.wave || [],
      });
    } catch (e) {
      setPeriodHistory({ consolidated: [], by_account: [], wave: [] });
    } finally { setPhLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => { fetchPeriodHistory(); }, [fetchPeriodHistory]);

  const generateReport = async () => {
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/treasury/report/generate`, { period, cfo_comment: cfoComment || null });
      toast({ title: 'Rapport généré', description: `Période ${data.period} · Statut : ${data.status}` });
      setCfoComment('');
      fetchAll();
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Échec', variant: 'destructive' });
    } finally { setGenerating(false); }
  };

  const downloadPdf = () => {
    const url = `${API}/treasury/report/pdf?period=${period}${cfoComment ? `&cfo_comment=${encodeURIComponent(cfoComment)}` : ''}`;
    window.open(url, '_blank');
  };

  const previewHtml = () => {
    const url = `${API}/treasury/report/html?period=${period}${cfoComment ? `&cfo_comment=${encodeURIComponent(cfoComment)}` : ''}`;
    window.open(url, '_blank');
  };

  const sendEmail = async () => {
    setSendingEmail(true);
    try {
      const { data } = await axios.post(`${API}/treasury/report/send-email`, { period, cfo_comment: cfoComment || null });
      toast({
        title: `Email envoyé — ${data.sent}/${data.total}`,
        description: data.sent === data.total ? 'Tous les destinataires ont bien reçu le rapport' : 'Certains envois ont échoué (voir historique)',
      });
      fetchAll();
    } catch (e) {
      toast({ title: 'Erreur envoi email', description: e?.response?.data?.detail || 'Échec', variant: 'destructive' });
    } finally { setSendingEmail(false); }
  };

  const sendWhatsApp = async () => {
    setSendingWA(true);
    try {
      const { data } = await axios.post(`${API}/treasury/report/send-whatsapp`, { period, cfo_comment: cfoComment || null });
      if (data.success) {
        const phonesArr = data.phones || (data.phone ? [data.phone] : []);
        const successCount = (data.results || []).filter(r => r.success).length || phonesArr.length;
        toast({
          title: 'WhatsApp envoyé',
          description: phonesArr.length > 1
            ? `Envoyé à ${successCount}/${phonesArr.length} destinataires : ${phonesArr.join(', ')}`
            : `Envoyé à ${phonesArr[0] || data.phone}`,
        });
      } else {
        const err = data.text_result?.error || 'Voir logs';
        const isSessionErr = /session|not.*connected|not.*ready|not.*enabled/i.test(err);
        const isAuthErr = /auth|token|unauth|401|403/i.test(err);
        toast({
          title: 'Échec WhatsApp',
          description: isSessionErr
            ? "Session WhatsApp non connectée. Rendez-vous dans Notifications → WhatsApp pour scanner le QR code."
            : isAuthErr
              ? "Authentification WhatsApp invalide. Vérifiez la session OpenWA dans Notifications → WhatsApp (re-scan du QR code si nécessaire)."
              : err,
          variant: 'destructive',
        });
      }
      fetchAll();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      toast({
        title: 'Erreur envoi WhatsApp',
        description: status === 503
          ? (detail || "WhatsApp non configuré. Configurez-le dans Notifications → WhatsApp.")
          : (detail || 'Échec inattendu — voir logs backend.'),
        variant: 'destructive',
      });
    } finally { setSendingWA(false); }
  };

  const closureBlocked = closure.fully_closed;

  /* Actions bar (header et embarqué) */
  const renderActions = () => (
    <>
      <button onClick={() => {
        const now = new Date();
        const opts = [-3, -2, -1, 0].map(offset => {
          const d = new Date(); d.setMonth(now.getMonth() + offset);
          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        });
        const choice = window.prompt(`Choisir la période (YYYY-MM) — options récentes : ${opts.join(', ')}`, period);
        if (choice && /^\d{4}-\d{2}$/.test(choice)) setPeriod(choice);
      }}
        className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
        data-testid="treasury-period-btn">
        <Calendar className="h-4 w-4 text-slate-500" />
        <span className="font-medium">{periodLabel(period)}</span>
        {closureBlocked && <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 bg-rose-100 text-rose-700 rounded-full"><Lock className="h-3 w-3" /> Clôturé</span>}
      </button>
      <button onClick={fetchAll}
        className="inline-flex items-center gap-1.5 border border-slate-200 rounded-lg px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50" data-testid="treasury-refresh-btn">
        <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Actualiser
      </button>
      <button onClick={generateReport} disabled={generating}
        className="inline-flex items-center gap-1.5 bg-emerald-600 text-white rounded-lg px-3 py-1.5 text-sm hover:bg-emerald-700 disabled:bg-slate-400" data-testid="treasury-generate-btn">
        {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
        Générer rapport
      </button>
      <button onClick={sendEmail} disabled={sendingEmail || !settings.send_email}
        className="inline-flex items-center gap-1.5 border border-blue-300 text-blue-700 bg-blue-50 rounded-lg px-3 py-1.5 text-sm hover:bg-blue-100 disabled:opacity-50" data-testid="treasury-send-email-btn">
        {sendingEmail ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
        Envoyer email
      </button>
      <button onClick={sendWhatsApp} disabled={sendingWA || !settings.send_whatsapp || !(settings.admin_whatsapp || (settings.extra_whatsapps || []).length > 0 || (settings.staff_user_ids || []).length > 0)}
        className="inline-flex items-center gap-1.5 border border-emerald-300 text-emerald-700 bg-emerald-50 rounded-lg px-3 py-1.5 text-sm hover:bg-emerald-100 disabled:opacity-50" data-testid="treasury-send-wa-btn">
        {sendingWA ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageCircle className="h-4 w-4" />}
        Envoyer WhatsApp
      </button>
      <button onClick={() => setSettingsOpen(true)}
        className="inline-flex items-center gap-1.5 border border-slate-300 text-slate-700 bg-white rounded-lg px-3 py-1.5 text-sm hover:bg-slate-50" data-testid="treasury-settings-btn">
        <Settings2 className="h-4 w-4" /> Paramètres
      </button>
      <SavedViewsMenu
        scope="treasury"
        currentFilters={{ period, tab }}
        onLoad={(f) => {
          if (f?.period) setPeriod(f.period);
          if (f?.tab) setTab(f.tab);
        }}
      />
    </>
  );

  /* ═════════════════════════════════════════════════════════════════ */

  return (
    <div className={embedded ? '' : 'min-h-screen bg-slate-50'} data-testid="treasury-global-page">
      {/* HEADER (masqué en mode embarqué — la page hôte a son propre header) */}
      {!embedded && (
        <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
          <div className="px-6 py-4 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white">
                <Wallet className="h-5 w-5" />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-slate-900" data-testid="treasury-title">
                  Trésorerie globale
                </h1>
                <p className="text-xs text-slate-500">
                  Bank + Wave consolidés · Cockpit CFO · dernière MAJ {summary.generated_at ? new Date(summary.generated_at).toLocaleTimeString('fr-FR') : '—'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">{renderActions()}</div>
          </div>
        </div>
      )}

      {/* ZONE PRINCIPALE */}
      <div className={embedded ? 'space-y-4' : 'px-6 py-5 space-y-4'}>
        {/* Barre d'actions en mode embarqué */}
        {embedded && (
          <div className="bg-white rounded-xl border border-slate-200 p-3 flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Wallet className="h-4 w-4 text-emerald-600" />
              <span className="text-sm font-semibold text-slate-800">Trésorerie globale</span>
              <span className="text-xs text-slate-500">· MAJ {summary.generated_at ? new Date(summary.generated_at).toLocaleTimeString('fr-FR') : '—'}</span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">{renderActions()}</div>
          </div>
        )}

        {/* HERO CARD */}
        <div className="rounded-2xl bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 text-white p-6 shadow-lg" data-testid="treasury-hero">
          <div className="text-xs uppercase tracking-wider opacity-80 font-medium">Trésorerie globale disponible</div>
          <div className="text-4xl lg:text-5xl font-bold mt-2 mb-3" data-testid="treasury-total">
            {fmt(summary.total)} <span className="text-lg opacity-70 font-normal">XOF</span>
          </div>
          <div className="grid grid-cols-2 gap-4 mt-2 max-w-md">
            <div className="border-l-2 border-white/30 pl-3">
              <div className="text-xs uppercase opacity-70">Bank</div>
              <div className="text-lg font-semibold">{fmt(summary.bank?.total)} XOF</div>
              <div className="text-xs opacity-70">{summary.bank?.accounts_count || 0} compte{(summary.bank?.accounts_count || 0) > 1 ? 's' : ''}</div>
            </div>
            <div className="border-l-2 border-white/30 pl-3">
              <div className="text-xs uppercase opacity-70">Wave</div>
              <div className="text-lg font-semibold">{fmt(summary.wave?.balance)} XOF</div>
              <div className="text-xs opacity-70">Variation 7j : {fmt(summary.wave?.variation_7d)}</div>
            </div>
          </div>
        </div>

        {/* KPI GRID (8 cards) */}
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
          <KpiCard testId="kpi-credits" icon={ArrowUpRight} label="Total entrées" value={kpis.total_credits} accent="green" />
          <KpiCard testId="kpi-debits" icon={ArrowDownRight} label="Total sorties" value={kpis.total_debits} accent="red" />
          <KpiCard testId="kpi-net" icon={TrendingUp} label="Variation nette" value={kpis.net_variation} accent={(kpis.net_variation || 0) >= 0 ? 'green' : 'red'} />
          <KpiCard testId="kpi-fees" icon={FileText} label="Frais financiers" value={kpis.total_fees} accent="orange" />
          <KpiCard testId="kpi-reconciled" icon={CheckCircle2} label="Rapprochées" value={kpis.reconciled_count} unit="tx" accent="green" />
          <KpiCard testId="kpi-unreconciled" icon={AlertTriangle} label="Non rapprochées" value={kpis.unreconciled_count} unit="tx" accent="orange" />
          <KpiCard testId="kpi-exceptions" icon={AlertTriangle} label="Exceptions" value={exceptions.total_count} unit="tx" accent={exceptions.total_count > 0 ? 'red' : 'slate'} />
          <KpiCard testId="kpi-closure" icon={Lock} label="Statut clôture" value={closure.fully_closed ? 'Clôturé' : 'Ouvert'} unit="" accent={closure.fully_closed ? 'blue' : 'orange'}
                   subline={`Bank ${closure.bank_locked ? '✓' : '○'} · Wave ${closure.wave_locked ? '✓' : '○'}`} />
        </div>

        {/* Commentaire CFO */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <label className="text-xs font-medium text-slate-700 uppercase tracking-wide">Commentaire CFO (inclus dans le rapport)</label>
          <textarea
            value={cfoComment}
            onChange={(e) => setCfoComment(e.target.value)}
            placeholder="Synthèse trésorerie, points d'attention, actions engagées…"
            rows={2}
            className="mt-2 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            data-testid="treasury-cfo-comment"
          />
          <div className="flex items-center gap-2 mt-2">
            <button onClick={previewHtml}
              className="text-xs inline-flex items-center gap-1 text-blue-600 hover:text-blue-800" data-testid="treasury-preview-html-btn">
              <ScrollText className="h-3.5 w-3.5" /> Aperçu HTML
            </button>
            <button onClick={downloadPdf}
              className="text-xs inline-flex items-center gap-1 text-emerald-600 hover:text-emerald-800" data-testid="treasury-download-pdf-btn">
              <Download className="h-3.5 w-3.5" /> Télécharger PDF
            </button>
          </div>
        </div>

        {/* TABS */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="border-b border-slate-200 px-2 flex gap-1 overflow-x-auto">
            {[
              { key: 'overview', label: 'Vue d\'ensemble', icon: TrendingUp },
              { key: 'analysis', label: 'Analyse & Prédiction', icon: Sparkles },
              { key: 'evolution', label: 'Évolution', icon: Calendar },
              { key: 'accounts', label: 'Comptes', icon: Building2 },
              { key: 'exceptions', label: `Exceptions (${exceptions.total_count})`, icon: AlertTriangle },
              { key: 'reports', label: `Rapports (${history.length})`, icon: History },
              { key: 'audit', label: 'Journal audit', icon: ScrollText },
            ].map(t => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${tab === t.key ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-slate-600 hover:text-slate-900'}`}
                data-testid={`treasury-tab-${t.key}`}
              >
                <t.icon className="h-4 w-4" />
                {t.label}
              </button>
            ))}
          </div>

          <div className="p-4">
            {tab === 'overview' && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                    <div className="text-xs uppercase text-emerald-700 font-semibold">Bank consolidé</div>
                    <div className="text-2xl font-bold text-emerald-900 mt-1">{fmt(kpis.bank?.total_credits)} XOF</div>
                    <div className="text-xs text-emerald-700 mt-1">Crédits sur la période</div>
                    <div className="mt-3 text-xs text-slate-700 space-y-1">
                      <div>Débits : {fmt(kpis.bank?.total_debits)} XOF</div>
                      <div>Frais : {fmt(kpis.bank?.total_fees)} XOF</div>
                      <div>Rapprochées : {kpis.bank?.reconciled} tx · Non rapprochées : {kpis.bank?.unreconciled} tx</div>
                    </div>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="text-xs uppercase text-blue-700 font-semibold">Wave Business</div>
                    <div className="text-2xl font-bold text-blue-900 mt-1">{fmt(kpis.wave?.credits)} XOF</div>
                    <div className="text-xs text-blue-700 mt-1">Encaissements sur la période</div>
                    <div className="mt-3 text-xs text-slate-700 space-y-1">
                      <div>Débits : {fmt(kpis.wave?.debits)} XOF</div>
                      <div>Frais Wave : {fmt(kpis.wave?.fees)} XOF</div>
                      <div>Rapprochées : {kpis.wave?.reconciled} tx · Non rapprochées : {kpis.wave?.unreconciled} tx</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {tab === 'analysis' && (
              <TreasuryAnalysisTab />
            )}

            {tab === 'evolution' && (
              <div className="space-y-4" data-testid="treasury-evolution-tab">
                <PeriodEvolutionCard
                  title="Trésorerie consolidée — évolution 12 mois"
                  subtitle="Solde de clôture Bank + Wave (ligne) · Crédits & débits mensuels (barres)"
                  data={periodHistory.consolidated}
                  loading={phLoading}
                />
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {periodHistory.by_account.map(acc => (
                    <PeriodEvolutionCard
                      key={acc.account_id}
                      title={`${acc.bank_name || 'Compte'} · ${acc.account_label || acc.account_number || ''}`}
                      subtitle="Évolution mensuelle par compte"
                      data={acc.periods}
                      loading={phLoading}
                    />
                  ))}
                  {periodHistory.wave && periodHistory.wave.length > 0 && (
                    <PeriodEvolutionCard
                      title="Wave Business · évolution mensuelle"
                      subtitle="Solde estimé à partir du snapshot live"
                      data={periodHistory.wave}
                      loading={phLoading}
                    />
                  )}
                </div>
              </div>
            )}

            {tab === 'accounts' && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                    <tr>
                      <th className="text-left px-3 py-2">Banque</th>
                      <th className="text-left px-3 py-2">N° compte</th>
                      <th className="text-right px-3 py-2">Solde</th>
                      <th className="text-right px-3 py-2">Variation 7j</th>
                      <th className="text-left px-3 py-2">Dernière opération</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(summary.bank?.accounts || []).map(a => (
                      <tr key={a.id} className="border-t border-slate-100" data-testid={`treasury-account-${a.id}`}>
                        <td className="px-3 py-2 font-medium">{a.bank_name}</td>
                        <td className="px-3 py-2 font-mono text-xs text-slate-500">{a.account_number}</td>
                        <td className="px-3 py-2 text-right font-mono font-semibold">{fmt(a.balance)} XOF</td>
                        <td className={`px-3 py-2 text-right font-mono ${(a.variation_7d || 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{fmt(a.variation_7d)} XOF</td>
                        <td className="px-3 py-2 text-xs text-slate-500">{a.last_operation_at ? new Date(a.last_operation_at).toLocaleDateString('fr-FR') : '—'}</td>
                      </tr>
                    ))}
                    <tr className="border-t border-slate-100 bg-blue-50">
                      <td className="px-3 py-2 font-medium">Wave Business</td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-500">wallet</td>
                      <td className="px-3 py-2 text-right font-mono font-semibold">{fmt(summary.wave?.balance)} XOF</td>
                      <td className="px-3 py-2 text-right font-mono text-slate-500">{fmt(summary.wave?.variation_7d)} XOF</td>
                      <td className="px-3 py-2 text-xs text-slate-500">{summary.wave?.last_sync_at ? new Date(summary.wave.last_sync_at).toLocaleDateString('fr-FR') : '—'}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {tab === 'exceptions' && (
              <div className="space-y-3">
                <div className="text-xs text-slate-500">{exceptions.total_count} anomalies détectées ({exceptions.bank_exceptions.length} Bank · {exceptions.wave_exceptions.length} Wave)</div>
                {exceptions.total_count === 0 ? (
                  <div className="p-8 text-center text-slate-400"><CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-emerald-500" /> Aucune anomalie détectée</div>
                ) : (
                  <div className="space-y-2">
                    {[...exceptions.bank_exceptions.map(e => ({ ...e, _src: 'Bank' })), ...exceptions.wave_exceptions.map(e => ({ ...e, _src: 'Wave' }))].slice(0, 50).map((e, i) => (
                      <div key={i} className="p-3 border border-slate-200 rounded-lg text-xs flex justify-between items-start">
                        <div>
                          <span className="font-medium">{e._src}</span> · {e.label || e.description || e._category_label || 'Anomalie'}
                          <div className="text-slate-500 mt-0.5">Ref : {e.reference || e.waveRef || e.id?.slice(0, 8)}</div>
                        </div>
                        <div className="text-slate-500">{fmt(Math.abs(e.amount || 0))} XOF</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {tab === 'reports' && (
              <div>
                {history.length === 0 ? (
                  <div className="p-8 text-center text-slate-400">
                    <FileText className="h-8 w-8 mx-auto mb-2 opacity-40" />
                    Aucun rapport historisé.<br/>Cliquez sur « Générer rapport » pour en créer un.
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                      <tr>
                        <th className="text-left px-3 py-2">Période</th>
                        <th className="text-left px-3 py-2">Généré le</th>
                        <th className="text-left px-3 py-2">Par</th>
                        <th className="text-right px-3 py-2">Total</th>
                        <th className="text-center px-3 py-2">Email</th>
                        <th className="text-center px-3 py-2">WhatsApp</th>
                        <th className="text-center px-3 py-2">Statut</th>
                        <th className="text-center px-3 py-2">PDF</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map(h => (
                        <tr key={h.id} className="border-t border-slate-100" data-testid={`treasury-history-${h.id}`}>
                          <td className="px-3 py-2 font-medium">{periodLabel(h.period)}</td>
                          <td className="px-3 py-2 text-xs text-slate-500">{new Date(h.generated_at).toLocaleString('fr-FR')}</td>
                          <td className="px-3 py-2 text-xs">{h.generated_by_email || '—'}</td>
                          <td className="px-3 py-2 text-right font-mono">{fmt(h.kpis_snapshot?.total)} XOF</td>
                          <td className="px-3 py-2 text-center">{h.email_sent ? '✓' : '—'}</td>
                          <td className="px-3 py-2 text-center">{h.whatsapp_sent ? '✓' : '—'}</td>
                          <td className="px-3 py-2 text-center">
                            <span className={`px-2 py-0.5 rounded text-[10px] ${h.status === 'ok' ? 'bg-emerald-100 text-emerald-700' : h.status === 'needs_review' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>
                              {h.status}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-center">
                            <a href={`${API}/treasury/report/${h.id}/download`} target="_blank" rel="noreferrer"
                               className="text-blue-600 hover:text-blue-800 inline-flex" data-testid={`treasury-download-${h.id}`}>
                              <Download className="h-3.5 w-3.5" />
                            </a>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {tab === 'audit' && (
              <div>
                {auditLog.length === 0 ? (
                  <div className="p-8 text-center text-slate-400">Journal vide</div>
                ) : (
                  <div className="space-y-1.5">
                    {auditLog.map(a => (
                      <div key={a.id} className="p-2.5 border border-slate-200 rounded-lg text-xs flex justify-between">
                        <div>
                          <span className="font-mono text-[11px] bg-slate-100 px-1.5 py-0.5 rounded">{a.action}</span>
                          <span className="text-slate-500 ml-2">par {a.user_email || a.user_id}</span>
                        </div>
                        <span className="text-slate-400">{new Date(a.timestamp).toLocaleString('fr-FR')}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <SettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)}
                     settings={settings} onSaved={(s) => setSettings(s)} />
    </div>
  );
}
